import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import css from './ProspectiveForm.css';
import { useToggle } from 'react-use';
import KioskImage from './KioskImage';
import StyledButton from 'common/StyledButton';
import Input from './Input';
import Toast from 'common/Toast';
import useStarfishAPI from '../hooks/useStarfishAPI';
import { validateField, validateProspectiveForm, isProspectiveFormValid } from 'utils/kiosk';
import moment from 'moment';
import PrivacyPolicyLink from '../common/PrivacyPolicyLink';

function ProspectiveForm(props) {
  // Form values
  const [formInputs, setFormInputs] = useState({
    firstName: { value: "", isValid: true, errorMessage: "" },
    lastName: { value: "", isValid: true, errorMessage: "" },
    email: { value: "", isValid: true, errorMessage: "" },
    emailConfirmation: { value: "", isValid: true, errorMessage: "" },
    birthday: { value: "", isValid: true, errorMessage: "" },
    phone: { value: "", isValid: true, errorMessage: "" },
    zipCode: { value: "", isValid: true, errorMessage: "" },
  });

  // Form controls
  const [showToast, toggleToast] = useToggle(false);
  const [hasSubmitted, setHasSubmitted] = useToggle(false);

  // Declarative callbacks for the starfish API
  const [{ data, success, pending, error }, callAPI] = useStarfishAPI();

  // If the API call is successful, sign user in and make sure isProspectiveStudent is true
  if ( success ) props.onSignIn({...data, studentEmail: formInputs.email.value}, true);

  // Every render check if form is valid, displays error message if that's not the case
  if ( !isProspectiveFormValid(formInputs) ) props.setError("Sign up could not be completed because there are errors in your form.");
  else if ( error ) props.setError(error);
  else props.setError("");

  const onInputChange = (field, value, comparison) => {
    let [isValid, errorMessage] = hasSubmitted ? validateField(field, value, comparison) : [true, ""];
    const newInputs = { ...formInputs, [field]: { value, isValid, errorMessage } };

    // We need to validate email and email confirmation when they change
    if ( hasSubmitted && field === 'email' ) {
      [isValid, errorMessage] = validateField('emailConfirmation', formInputs.emailConfirmation.value, value);
      newInputs['emailConfirmation'] = { value: formInputs.emailConfirmation.value, isValid, errorMessage };
    } else if ( hasSubmitted && field === 'emailConfirmation' ) {
      [isValid, errorMessage] = validateField('email', formInputs.email.value, value);
      newInputs['email'] = { value: formInputs.email.value, isValid, errorMessage };
    }

    setFormInputs(newInputs);
  }

  const isSubmitButtonDisabled = () => pending || (hasSubmitted && !isProspectiveFormValid(formInputs));
  
  const onSubmit = e => {
    e.preventDefault();
    if ( isSubmitButtonDisabled() ) return false;
    setHasSubmitted(true);
    const validatedForm = validateProspectiveForm(formInputs, props.prospectsNeedBirthday, props.prospectsNeedPhone, props.prospectsNeedZip);
    setFormInputs(validatedForm);
    if ( isProspectiveFormValid(validatedForm) ) {

      let body = {
        firstName: validatedForm.firstName.value,
        lastName: validatedForm.lastName.value,
        secondaryEmail: validatedForm.email.value,
        confirmEmail: validatedForm.emailConfirmation.value,
        isProspectiveStudent: true,
        kioskId: props.kioskId,
        kioskUid: props.kioskUid,
        shortForm: false }

        if (props.prospectsNeedZip) {
          body.homeZip = validatedForm.zipCode.value;
        }
        if (props.prospectsNeedPhone) {
          body.homePhone = validatedForm.phone.value;
        }

        if (props.prospectsNeedBirthday) {
          body.birthDate = moment(validatedForm.birthday.value, "MM/DD/YYYY").format("YYYY-MM-DD");
        }
      const URL = `../kiosk/signInProspectWalkIn.html`;
      callAPI(URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
    } else toggleToast(true);
  }
  // move page to top when navigated from previous page.
  useEffect(() => {
      props.contentRef.current.scrollTop = 0;
  }, []);

  return (
    <>
      <div className={css['container']}>
        <header>
          <KioskImage name={props.kioskName} imageUrl={props.kioskImageUrl} />
          <h1>{props.mainHeader}</h1>
          <h2>
            Already signed up? 
            {' '}
            <StyledButton 
              className={css['login-button']} 
              asLink 
              onClick={props.goToProspectiveSignIn}>
              Login using your email address.
            </StyledButton>
          </h2>
          {props.instructionsText && <h3>{props.instructionsText}</h3>}
          <p>
            By completing this form, you are agreeing to this
            {' '}
            <PrivacyPolicyLink />.
          </p>
        </header>
        <form onSubmit={onSubmit}>
          <Input
            id="kiosk-first-name-field"
            autoFocus
            type="text"
            placeholder="First Name"
            label="First Name"
            value={formInputs.firstName.value}
            onChange={e => onInputChange('firstName', e.target.value)}
            errorMessage={formInputs.firstName.errorMessage}
          />
          <Input
            id="kiosk-last-name-field"
            type="text"
            placeholder="Last Name"
            label="Last Name"
            value={formInputs.lastName.value}
            onChange={e => onInputChange('lastName', e.target.value)}
            errorMessage={formInputs.lastName.errorMessage}
          />
          <Input
            id="kiosk-email-field"
            type="email"
            placeholder="Personal Email"
            label="Personal Email"
            value={formInputs.email.value}
            onChange={e => onInputChange('email', e.target.value, formInputs.emailConfirmation.value)}
            errorMessage={formInputs.email.errorMessage}
          />
          <Input
            id="kiosk-email-confirmation-field"
            type="email"
            placeholder="Confirm Email"
            label="Confirm Email"
            value={formInputs.emailConfirmation.value}
            onChange={e => onInputChange('emailConfirmation', e.target.value, formInputs.email.value)}
            errorMessage={formInputs.emailConfirmation.errorMessage}
          />
          {props.prospectsNeedBirthday && <Input
            id="kiosk-birthday-field"
            type="text"
            placeholder="Birthday"
            label="Birthday"
            value={formInputs.birthday.value}
            onChange={e => onInputChange('birthday', e.target.value)}
            errorMessage={formInputs.birthday.errorMessage}
            hint="MM/DD/YYYY"
          />}
          {props.prospectsNeedPhone && <Input
            id="kiosk-phone-field"
            type="phone"
            placeholder="Home Phone"
            label="Home Phone"
            value={formInputs.phone.value}
            onChange={e => onInputChange('phone', e.target.value)}
            errorMessage={formInputs.phone.errorMessage}
          />}
          {props.prospectsNeedZip && <Input
            id="kiosk-zip-code-field"
            type="text"
            placeholder="Postal Code"
            label="Postal Code"
            value={formInputs.zipCode.value}
            onChange={e => onInputChange('zipCode', e.target.value)}
            errorMessage={formInputs.zipCode.errorMessage}
          />}
          <h2>Please note that all of the fields are mandatory in order to complete sign up.</h2>
          <StyledButton
            aria-disabled={isSubmitButtonDisabled()} 
            className={css['sign-up-button']} 
            type="submit">
            {pending ? "Signing Up..." : "Sign Up"}
          </StyledButton>
        </form>
        <StyledButton className={css['back-button']} asLink onClick={props.goBack}>
          Go back.
        </StyledButton>
      </div>
      {showToast && <Toast 
        message="Please fix the errors to complete sign up." 
        onTimeout={() => toggleToast(false)} 
      />}
    </>
  );
}

ProspectiveForm.propTypes = {
  kioskId: PropTypes.number.isRequired,
  kioskUid: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  kioskName: PropTypes.string.isRequired,
  kioskImageUrl: PropTypes.string.isRequired,
  prospectsNeedBirthday: PropTypes.bool.isRequired,
  prospectsNeedPhone: PropTypes.bool.isRequired,
  prospectsNeedZip: PropTypes.bool.isRequired,
  setError: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  goToProspectiveSignIn: PropTypes.func.isRequired,
  mainHeader: PropTypes.string,
  instructionsText: PropTypes.string,
  contentRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.any })
  ])
}

export default ProspectiveForm;
